/**
 * AbilityBuilderFactory — Runtime construction of ability functions from
 * a captured BuildSnapshot.
 *
 * Separates the fluent configuration API (AbilityBuilder) from the runtime
 * logic that wires behaviors, metadata, passives, toggles, and timelines.
 */

import { gameLogger } from '../../../../utils/GameLogger';
import { EventBus } from '../../../../shared/EventBus';
const eventBus = EventBus.getInstance();

export { applyResourceDrain } from './AbilityToggleRuntime';
import { createToggleAbility } from './AbilityToggleRuntime';
import { createAbilityWindup } from './AbilityWindupRuntime';
import { ActionContext } from './types';
import { AbilityMetadata } from './AbilityRegistry';
import type { AbilityFunction, ProjectileConfig, VFXConfig, BuffConfig } from './AbilityBuilderTypes';
import { VFXEffectRegistry, VFXPrimitive } from '../../../effects/VFXEffectRegistry';
import { AOEConfig } from './behaviors/AOEBehavior';
import { DashConfig } from './behaviors/DashBehavior';
import { ChannelConfig } from './behaviors/ChannelBehavior';
import { HealConfig } from './behaviors/HealBehavior';
import { ShieldConfig } from './behaviors/ShieldBehavior';
import { DebuffConfig } from './behaviors/DebuffBehavior';
import { MeleeConfig } from './behaviors/MeleeBehavior';
import { playerCombatPoint } from '../../../../shared/logic/CombatAnchors';
import { HitscanConfig } from './behaviors/HitscanBehavior';
import { SummonConfig } from './behaviors/SummonBehavior';
import type { AuraEffectConfig } from './behaviors/AuraEffectBehavior';
import { NovaProjectileConfig } from './behaviors/NovaProjectileBehavior';
import type { TimelineEntry } from './timeline/AbilityTimeline';
import type { ProjectileModifiers, HitEffectEntry, ConditionFn, AbilityModifier } from './modifiers/types';
import { withCastOverrides } from './modifiers/CastOverrides';
import { populateBehaviorMetadata, deriveTargetingAndMechanic, type BuilderConfigs } from './BehaviorRegistry';
import { lazyBehaviors } from './LazyBehaviors';
import { registerAbilitySessionReleaseHook, registerAbilitySessionRearmHook } from './AbilitySessionLifecycle';

export interface BuildSnapshot {
  abilityId: string;
  metadata: Partial<AbilityMetadata>;
  windUpDuration: number;
  windUpVFX: string | null;
  projectileConfig: ProjectileConfig | null;
  projectileVFX: VFXConfig | null;
  instantEffect: ((ctx: ActionContext) => void) | null;
  buffConfigs: BuffConfig[];
  multiProjectiles: { count: number; stagger: number; config: ProjectileConfig } | null;
  aoeConfig: AOEConfig | null;
  dashConfig: DashConfig | null;
  channelConfig: ChannelConfig | null;
  healConfig: HealConfig | null;
  shieldConfig: ShieldConfig | null;
  debuffConfig: DebuffConfig | null;
  onCompleteCallback: (() => void) | null;
  composedVFXLayers: VFXPrimitive[] | null;
  timelineDuration: number | null;
  timelineEntries: TimelineEntry[];
  projectileModifiers: ProjectileModifiers;
  hitEffects: HitEffectEntry[];
  conditionals: Array<{ condition: ConditionFn; modifier: AbilityModifier }>;
  meleeConfig: MeleeConfig | null;
  hitscanConfig: HitscanConfig | null;
  summonConfig: SummonConfig | null;
  chainInto: { abilityId: string; windowMs: number } | null;
  novaConfig: NovaProjectileConfig | null;
  passiveConfig: { trigger: string; chance?: number; cooldownMs?: number; effect: (ctx: ActionContext) => void } | null;
  toggleConfig: { resourceDrainPerSecond?: number; onActivate?: (ctx: ActionContext) => void; onDeactivate?: (ctx: ActionContext) => void; maxDurationMs?: number } | null;
  chargesConfig: { maxCharges: number; regenTimeMs: number; consumePerCast: number } | null;
  coneAOEConfig: { damage: number | ((ctx: ActionContext) => number); angle: number; distance: number; ticks?: number; vfxId?: string } | null;
  lineAOEConfig: { damage: number | ((ctx: ActionContext) => number); length: number; width: number; ticks?: number; vfxId?: string } | null;
  auraEffectConfig: AuraEffectConfig | null;
  /** Character-anchored glow aura on the CASTER (S2.F3 / theme F). Emits a
   *  vfx:status_effect for the caster → StatusVFXHandler → FollowerSystem
   *  keeps it on the sprite center. Purely cosmetic (no damage). */
  casterAuraConfig: CasterAuraConfig | null;
}

export interface CasterAuraConfig {
  /** Registered effect id, or resolves to `${abilityId}_aura`. */
  effectId: string;
  /** Lifetime (ms) before the aura is removed. */
  durationMs: number;
  /** Optional tint override (else the effect's own / authored palette color). */
  color?: number;
}

export function createAbilityFunction(snapshot: BuildSnapshot): AbilityFunction {
  const {
    abilityId,
    metadata,
    windUpDuration,
    windUpVFX,
    projectileConfig,
    projectileVFX,
    instantEffect,
    buffConfigs,
    multiProjectiles,
    aoeConfig,
    dashConfig,
    channelConfig,
    healConfig,
    shieldConfig,
    debuffConfig,
    onCompleteCallback,
    composedVFXLayers,
    timelineDuration,
    timelineEntries,
    projectileModifiers,
    hitEffects,
    conditionals,
    meleeConfig,
    hitscanConfig,
    summonConfig,
    chainInto,
    novaConfig,
    passiveConfig,
    toggleConfig,
    chargesConfig,
    coneAOEConfig,
    lineAOEConfig,
    auraEffectConfig,
    casterAuraConfig,
  } = snapshot;

  // Collect configs for BehaviorRegistry
  const configs: BuilderConfigs = {
    instantEffect,
    buffConfigs,
    aoeConfig,
    dashConfig,
    channelConfig,
    healConfig,
    shieldConfig,
    debuffConfig,
    meleeConfig,
    hitscanConfig,
    summonConfig,
    novaConfig,
    auraEffectConfig,
    coneAOEConfig,
    lineAOEConfig,
    multiProjectiles,
    projectileConfig,
    passiveConfig,
    toggleConfig,
    windUpDuration,
  };

  // withVFX() writes the PROJECTILE muzzle-flash slot — for non-projectile
  // abilities (dash/heal/channel: shimmer, disengage, rapid_fire,
  // holy_radiance) the declared effect was WRITE-ONLY and the ability rendered
  // nothing (census-dead, 2026-07-16 art pass). Play it as a caster-anchored
  // beat on execution instead, composing with any existing instant effect.
  //
  // Same-id dedupe (K28): ~186 manual modules declare the SAME effect id on
  // BOTH a behavior config (aoe/melee/heal/channel/summon/cone/line vfxId)
  // and .withVFX(). For them the pre-revival single play was the authored
  // baseline — playing the beat too renders the id twice per cast
  // (sinister_strike sidecars: two ability_cast_vfx rows per cast). Skip the
  // beat when a configured behavior provably plays the same id at execute.
  // A melee config's id (its vfxId, else this withVFX id) is the swing's IMPACT:
  // since plan 29 F4 it plays on the target from the confirmed hit, not at
  // execute, so it stays listed — the beat must not play it at the caster.
  //
  // INVARIANT (why this list is what it is — A4 census, 2026-07-17):
  // `behaviorBurstIds` enumerates ONLY behaviors that emit a `vfx:spawn`
  // one-shot BURST at execute — the same channel the beat below emits. That
  // is why status-channel behaviors (shield/debuff/buff → `vfx:status_effect`
  // persistent auras, resolved via resolveStatusVfxId) are DELIBERATELY absent:
  // a persistent aura cannot collide with the beat's burst, so 36 modules that
  // pair `.withDebuff({vfxId:x})` + `.withVFX(x)` (same id) correctly render one
  // aura + one beat. Do NOT add a status behavior's vfxId here, and do NOT
  // delete a slot as "redundant": flip any of these behaviors to `vfx:spawn`
  // (or add a new burst-emitting behavior) and the K28 double returns silently
  // unless its burst id is added to this list.
  const hasProjectileFamily = !!(projectileConfig || multiProjectiles || novaConfig || hitscanConfig);
  const behaviorBurstIds = [
    aoeConfig?.vfxId,
    aoeConfig?.windUpVfxId,
    meleeConfig ? (meleeConfig.vfxId ?? projectileVFX?.effectId) : undefined,
    healConfig?.vfxId,
    channelConfig?.vfxId,
    summonConfig?.vfxId,
    coneAOEConfig?.vfxId,
    lineAOEConfig?.vfxId,
  ].filter((id): id is string => typeof id === 'string');
  // .withVFX() writes the projectile muzzle slot (projectileVFX); the 2026-07-16
  // revival additionally plays it as a caster beat for non-projectile abilities.
  // Suppress that beat when a behavior burst already owns the id (K28).
  const beatIdOwnedByBehaviorBurst = !!projectileVFX?.effectId && behaviorBurstIds.includes(projectileVFX.effectId);
  const casterBeat = projectileVFX?.effectId && !hasProjectileFamily && !beatIdOwnedByBehaviorBurst ? projectileVFX : null;
  if (casterBeat) {
    const declared = casterBeat;
    const declaredEffectId = casterBeat.effectId as string;
    const prevInstant = configs.instantEffect;
    configs.instantEffect = (ctx: ActionContext) => {
      prevInstant?.(ctx);
      const player = ctx.currentPlayer as any;
      EventBus.getInstance().emit('vfx:spawn', {
        effectId: declaredEffectId,
        ...playerCombatPoint(player), // plan 29 F7: the body centre, not the hitbox corner
        duration: declared.duration ?? 400,
        color: declared.color,
        scale: declared.scale,
        abilityId,
        casterId: player.id,
        follow: false,
      });
    };
  }

  // 1. Behaviors are instantiated on the first cast, not here (LazyBehaviors).
  const lazy = lazyBehaviors(abilityId, configs, metadata, { duration: timelineDuration, entries: timelineEntries });
  const behaviors = lazy.list;

  // Behavior metadata for dashboards + the cast's visible geometry (plan 29 F5)
  populateBehaviorMetadata(metadata, configs, { casterAura: casterAuraConfig, beat: casterBeat, windUpMs: windUpVFX && !(projectileConfig || multiProjectiles) ? windUpDuration : 0, windUpEffectId: windUpVFX });

  // Derive targeting and mechanic type for contract alignment
  deriveTargetingAndMechanic(metadata, configs);

  // Attach configured data to metadata for UI sliders
  if (projectileConfig || multiProjectiles) {
    const config = projectileConfig || multiProjectiles!.config;
    if (!metadata.projectile) {
      metadata.projectile = {
        damage: typeof config.damage === 'number'
          ? (multiProjectiles ? `${config.damage} x ${multiProjectiles.count}` : config.damage)
          : 'Scaling',
        speed: config.speed,
        shape: config.shape,
        color: config.color,
        homing: typeof config.homing === 'object'
          ? {
            strength: config.homing.strength ?? 0,
            maxTurnRate: config.homing.maxTurnRate ?? 0,
            delay: config.homing.delay ?? 0
          }
          : undefined
      };
    }
  }

  if (windUpVFX || projectileVFX) {
    if (!metadata.vfx) metadata.vfx = {};

    if (windUpVFX) {
      metadata.vfx.windUp = windUpVFX;
    }
    if (projectileVFX) {
      metadata.vfx.muzzleFlash = projectileVFX.effectId;
      metadata.vfx.color = projectileVFX.color;
      metadata.vfx.scale = projectileVFX.scale;
      metadata.vfx.duration = projectileVFX.duration;
      // Non-projectile abilities play this as their cast beat (see the
      // execution hook above) — surface it in the cast slot for contracts.
      if (!hasProjectileFamily && !metadata.vfx.cast) {
        metadata.vfx.cast = projectileVFX.effectId;
      }
    }
  }

  // Auto-derive VFX metadata from behavior configs (scale-ready: future abilities
  // that only use withMelee({vfxId}) or withBuff({vfx}) automatically pass VFX contracts)
  if (!metadata.vfx || Object.keys(metadata.vfx).length === 0) {
    const derivedVfx: Record<string, string> = {};
    if (meleeConfig?.vfxId) derivedVfx.cast = meleeConfig.vfxId;
    if (aoeConfig?.vfxId) derivedVfx.cast = aoeConfig.vfxId;
    if (healConfig?.vfxId) derivedVfx.cast = healConfig.vfxId;
    if (summonConfig?.vfxId) derivedVfx.cast = summonConfig.vfxId;
    const explicitBuffVfx = buffConfigs.find((config) => config.vfx?.effectId)?.vfx?.effectId;
    if (explicitBuffVfx) derivedVfx.aura = explicitBuffVfx;
    if (Object.keys(derivedVfx).length > 0) {
      metadata.vfx = { ...derivedVfx, ...(metadata.vfx || {}) };
    }
  }

  // Handle composed VFX layers: register a composite effect combining cherry-picked primitives
  if (composedVFXLayers && composedVFXLayers.length > 0) {
    const compositeId = `${abilityId}_composed_vfx`;
    VFXEffectRegistry.register({
      id: compositeId,
      name: `${abilityId} Composed VFX`,
      category: 'burst',
      primitives: composedVFXLayers,
    });
    if (!metadata.vfx) metadata.vfx = {};
    metadata.vfx.muzzleFlash = compositeId;
  }

  // Attach projectile modifiers to metadata
  const hasModifiers = Object.keys(projectileModifiers).length > 0;
  if (hasModifiers && metadata.projectile) {
    metadata.projectile.modifiers = projectileModifiers;
  }

  // Attach on-hit effects to metadata
  if (hitEffects.length > 0) {
    metadata.onHitEffects = hitEffects;
  }

  // 3. Capture conditionals for runtime evaluation
  const capturedConditionals = conditionals.length > 0 ? [...conditionals] : null;

  // 4. Handle passive abilities (register listener instead of castable function)
  //
  // State is per-player so two players with the same passive don't share
  // proc-cooldown clocks. Re-casting the same passive on the same player is
  // a no-op (we don't stack listeners). Each registration captures the
  // EventBus unsubscribe handle so we can release it on player removal.
  if (passiveConfig) {
    const passiveCfg = passiveConfig;
    interface PassiveState {
      playerObject: object;
      playerId: string;
      /** Current cast context — replaced on rekey so procs never use a stale one. */
      ctx: ActionContext;
      lastProcTime: number;
      unsubscribe: () => void;
    }
    const stateById = new Map<string, PassiveState>();
    const stateByPlayer = new WeakMap<object, PassiveState>();
    /**
     * Registrations parked by a session-scoped release, awaiting the reconnect.
     * A passive arms when its ability function runs and nothing re-runs it
     * after a reconnect, so without this the character loses every passive
     * until the player re-casts it by hand (OPEN-17).
     */
    let dormant: Array<{ playerObject: object; ctx: ActionContext; lastProcTime: number }> = [];
    let unregisterReleaseHook: (() => void) | null = null;
    let unregisterRearmHook: (() => void) | null = null;

    const releasePlayer = (playerId?: string) => {
      const ids = playerId ? [playerId] : [...stateById.keys()];
      for (const id of ids) {
        const state = stateById.get(id);
        if (!state) continue;
        state.unsubscribe();
        stateById.delete(id);
        stateByPlayer.delete(state.playerObject);
        // Session-scoped release means a reconnect is expected: park it.
        // An UNSCOPED release is teardown/logout and must discard everything,
        // or logging out and back in as another character would revive the
        // first one's passives against a dead player object.
        if (playerId) dormant.push({ playerObject: state.playerObject, ctx: state.ctx, lastProcTime: state.lastProcTime });
      }
      if (!playerId) dormant = [];
      // Only stand the hooks down when there is genuinely nothing left —
      // dropping them while a registration is parked would strand it, which is
      // the same silent loss this dormancy exists to prevent.
      if (stateById.size === 0 && dormant.length === 0) {
        unregisterReleaseHook?.();
        unregisterReleaseHook = null;
        unregisterRearmHook?.();
        unregisterRearmHook = null;
      }
    };

    /** Arm one passive registration and return its state. */
    const arm = (
      playerObject: object,
      playerId: string,
      ctx: ActionContext,
      lastProcTime = 0,
    ): PassiveState => {
      const triggerEvent = `combat:${passiveCfg.trigger}`;
      const state: PassiveState = {
        playerObject, playerId, ctx, lastProcTime, unsubscribe: () => undefined,
      };
      // Resolve through the mutable state object, so a later rebind is picked
      // up without touching this subscription.
      state.unsubscribe = eventBus.onDynamic(triggerEvent, () => {
        if (stateById.get(state.playerId) !== state) return;   // released
        const now = Date.now();
        if (passiveCfg.cooldownMs && now - state.lastProcTime < passiveCfg.cooldownMs) return;
        if (passiveCfg.chance !== undefined && Math.random() > passiveCfg.chance) return;
        state.lastProcTime = now;
        passiveCfg.effect(state.ctx);
      });
      stateById.set(playerId, state);
      stateByPlayer.set(playerObject, state);
      return state;
    };

    const rearmPlayer = (sessionId: string) => {
      if (dormant.length === 0) return;
      const parked = dormant;
      dormant = [];
      for (const entry of parked) {
        if (stateByPlayer.has(entry.playerObject)) continue;   // already re-armed by a cast
        (entry.ctx.currentPlayer as { id: string }).id = sessionId;
        arm(entry.playerObject, sessionId, entry.ctx, entry.lastProcTime);
      }
    };

    const ensureReleaseHook = () => {
      unregisterReleaseHook ??= registerAbilitySessionReleaseHook(releasePlayer);
      unregisterRearmHook ??= registerAbilitySessionRearmHook(rearmPlayer);
    };

    const fn: AbilityFunction = (ctx: ActionContext) => {
      const playerId = ctx.currentPlayer.id;
      const playerObject = ctx.currentPlayer as object;

      // A reconnect can hand us a new player OBJECT, a new session ID, or both
      // for the same local character. Whichever moved, rebind the one existing
      // registration rather than adding a second listener beside it.
      //
      // Both lookups previously fell through to `if (stateById.has(playerId))
      // return;`, which registered nothing and left the old closure resolving
      // the old object: a rekey with a new id double-procced every passive,
      // and a rekey keeping the id never re-armed and kept firing against a
      // stale context.
      const existing = stateByPlayer.get(playerObject) ?? stateById.get(playerId);
      if (existing) {
        if (existing.playerId !== playerId) {
          stateById.delete(existing.playerId);
          existing.playerId = playerId;
          stateById.set(playerId, existing);
        }
        if (existing.playerObject !== playerObject) {
          stateByPlayer.delete(existing.playerObject);
          existing.playerObject = playerObject;
          stateByPlayer.set(playerObject, existing);
        }
        // The proc must run against the CURRENT context, not the one captured
        // when this passive first armed.
        existing.ctx = ctx;
        return;
      }

      arm(playerObject, playerId, ctx);
      ensureReleaseHook();
      gameLogger.info('abilities', `${abilityId} passive registered for ${playerId} on combat:${passiveCfg.trigger}`);
    };
    (fn as any).metadata = metadata;
    (fn as any).releasePlayer = releasePlayer;
    return fn;
  }

  if (toggleConfig) {
    const toggle = createToggleAbility(abilityId, metadata, toggleConfig, behaviors);
    const fn: AbilityFunction = (ctx: ActionContext) => {
      lazy.ensure();
      return toggle(ctx);
    };
    return Object.assign(fn, toggle);
  }

  // 6. Handle charge system wrapper (reserved for future implementation)
  void chargesConfig;

  const runComposedBehaviors = createAbilityWindup({ abilityId, metadata, behaviors,
    windUpDuration, windUpVFX, hasProjectile: !!(projectileConfig || multiProjectiles), casterAuraConfig });

  // 7. The Final Ability Function
  const fn: AbilityFunction = (ctx: ActionContext) => {
    lazy.ensure();
    if (behaviors.length === 0) {
      gameLogger.warn('abilities', `${abilityId} has no configured behaviors`);
      return;
    }

    // Keep conditional state local even when the behavior schedules delayed work.
    const castContext = withCastOverrides(ctx, capturedConditionals ?? []);
    runComposedBehaviors(castContext);

    if (onCompleteCallback) {
      onCompleteCallback();
    }

    // Combo chain tracking — HALF-WIRED.
    // The AbilityEditor UI lets authors set chainInto.abilityId + windowMs
    // (ComposeTab.tsx), the builder records it on metadata, and we emit
    // ability:combo_window_open here. No consumer currently *listens* for
    // that event or reads player.lastAbilityId / comboWindowEnd, so the
    // runtime markers are inert. QA tracks this as `mod_combo_chain`
    // in qaTemplates.ts. Wire a CombatHandler / AbilityRegistry consumer
    // before relying on combo-window behavior.
    if (chainInto) {
      const player = ctx.currentPlayer as any;
      player.lastAbilityId = abilityId;
      player.comboWindowEnd = Date.now() + (chainInto.windowMs || 2000);
    }
  };

  // Attach metadata to the function so it can be retrieved by registry
  (fn as any).metadata = metadata;
  return fn;
}
